import { motion } from "framer-motion";
import { Area, AreaChart, Bar, BarChart, ResponsiveContainer } from "recharts";
import { ArrowUpRight } from "lucide-react";
import { painel } from "@/content";

// Dados abstratos apenas para a visualização (não representam resultados reais)
const wave = [8, 12, 10, 16, 14, 20, 18, 25, 22, 29, 27, 34].map((v, i) => ({ i, a: v, b: v * 0.62 + 4 }));
const bars = [5, 8, 6, 9, 7, 11, 10, 13].map((v, i) => ({ i, v }));
const spark = (seed) => [3, 5, 4, 6, 5, 7, 8].map((v, i) => ({ i, v: v + ((i * seed) % 3) }));

const Float = ({ children, className, delay = 0 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: [0, -8, 0] }}
    transition={{ opacity: { duration: 0.8, delay }, y: { duration: 6, repeat: Infinity, ease: "easeInOut", delay } }}
  >
    {children}
  </motion.div>
);

const MetricCard = ({ m, idx }) => (
  <div data-testid={`dashboard-metric-${idx}`} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
    <div className="flex items-center justify-between text-[11px] text-slate-400">
      {m.label}
      <ArrowUpRight size={12} className="text-cyan-300" />
    </div>
    <div className="mt-2 h-8">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={spark(idx + 2)}>
          <Area type="monotone" dataKey="v" stroke="#22d3ee" strokeWidth={1.5} fill="#22d3ee" fillOpacity={0.12} isAnimationActive />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </div>
);

export const HeroDashboard = () => (
  <div data-testid="hero-dashboard" className="relative mx-auto w-full max-w-xl">
    <div className="absolute -inset-10 rounded-full bg-blue-600/20 blur-3xl" />
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
      className="glass relative rounded-3xl p-5 sm:p-6"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="font-display text-sm font-semibold text-white">{painel.titulo}</p>
          <p className="text-[11px] text-slate-500">{painel.legenda}</p>
        </div>
        <div className="flex gap-1.5">
          <span className="h-2 w-2 animate-pulse rounded-full bg-cyan-300 shadow-[0_0_10px_#22d3ee]" />
          <span className="h-2 w-2 rounded-full bg-blue-500/60" />
          <span className="h-2 w-2 rounded-full bg-white/20" />
        </div>
      </div>

      <div className="mt-5 h-44 sm:h-52">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={wave} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="gA" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#22d3ee" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gB" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area type="monotone" dataKey="b" stroke="#3b82f6" strokeWidth={1.5} fill="url(#gB)" animationDuration={1800} />
            <Area type="monotone" dataKey="a" stroke="#22d3ee" strokeWidth={2.5} fill="url(#gA)" animationDuration={2200}
              style={{ filter: "drop-shadow(0 0 8px rgba(34,211,238,0.7))" }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        {painel.metricas.map((m, i) => <MetricCard key={m.label} m={m} idx={i} />)}
      </div>
    </motion.div>

    <Float delay={0.6} className="glass absolute -left-4 -bottom-24 hidden w-44 rounded-2xl p-4 sm:block lg:-left-16">
      <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Indicadores</p>
      <div className="mt-3 h-16">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={bars}>
            <Bar dataKey="v" fill="#3b82f6" radius={[3, 3, 0, 0]} animationDuration={1600} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Float>

    <Float delay={1} className="glass absolute -right-4 -top-8 hidden rounded-2xl p-4 sm:block lg:-right-10">
      <ul className="space-y-2">
        {painel.indicadores.map((ind, i) => (
          <li key={ind} className="flex items-center gap-3 text-xs text-slate-300">
            <span className="h-1.5 rounded-full bg-gradient-to-r from-cyan-300 to-blue-500" style={{ width: 18 + i * 10 }} />
            {ind}
          </li>
        ))}
      </ul>
    </Float>
  </div>
);
